/**
 * Distance Measure Tool
 *
 * Allows measuring distances between points (multi-point line measurement).
 * Each click adds a point. Double-click or Enter finishes. Right-click undoes.
 * Closing the polygon by hovering the first point converts to an AreaAnnotation.
 */

import * as THREE from 'three';
import { MeasureToolBase } from './MeasureToolBase';
import { DragEventData } from '../../BaseTool';
import { MeasureTools } from '../../../utils/Measure';
import { AreaAnnotation, DistanceAnnotation, PointoramaAnnotation } from '../../../utils/PointoramaAnnotation';

export class DistanceMeasurePersonaTool extends MeasureToolBase {
  readonly name = 'distance_measure';
  readonly measureType = MeasureTools.DISTANCE;
  protected readonly minSpheresToFinish = 2; // 2 placed points + 1 preview = 3 spheres

  private shiftKeyPressed = false;

  protected createAnnotation(): PointoramaAnnotation {
    return new DistanceAnnotation({ viewer: this.viewer, name: this.viewer.translationService.t('line', { swc: true }) });
  }

  // =========================================================================
  // Mouse event handlers
  // =========================================================================

  public onMouseMove(event: MouseEvent): void {
    if (this.isDraggingSphere()) return;

    const mouse = this.getMouseFromEvent(event);

    this.updateHoverState();
    this.drawNewMeasure(mouse, this.shiftKeyPressed);
    this.updateVisuals();
  }

  public onClick(_event: MouseEvent): void {
    // Plane click handling (3D projection planes)
    if (this.planeSnapping.getHoveredPlane()) {
      this.planeSnapping.onClick(THREE.MOUSE.LEFT);
      this.planeSnapping.updateHover();
      return;
    }

    if (!this.canPlacePoint()) return;

    // Closing snap: hovering first point → close as AreaAnnotation
    if (this.hoveringFirstPoint()) {
      const newPolygon = new AreaAnnotation({
        viewer: this.viewer,
        name: this.viewer.translationService.t('polygon', { swc: true }),
        points: [...this.currentMeasure.points],
      });
      this.measureScene.remove(this.currentMeasure);
      this.currentMeasure = newPolygon;
      this.finishCurrentMeasurement();
      return;
    }

    this.placePoint();
  }

  public onDoubleClick(_event: MouseEvent): void {
    this.finishCurrentMeasurement();
  }

  public onRightClick(_event: MouseEvent): void {
    // Right-click on existing sphere when not drawing: delete that point
    // (hovered sphere can be stale/detached until the next mouse move)
    if (this.hoveredSphere?.parent && !this.drawingInProgress()) {
      const measure = this.hoveredSphere.parent as PointoramaAnnotation;
      const pointIndex = measure.getSphereIndex(this.hoveredSphere);
      this.removeMarker(measure, pointIndex);
      return;
    }

    // During drawing: undo last placed point (only affects currentMeasure)
    this.undoCurrentMeasure();
  }

  // =========================================================================
  // Keyboard handlers
  // =========================================================================

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      this.shiftKeyPressed = true;
      return;
    }

    if (event.key === 'Escape') {
      if (this.drawingInProgress()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.startMeasure();
      }
      return;
    }

    if (event.key === 'Enter') {
      this.finishCurrentMeasurement();
      return;
    }
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      this.shiftKeyPressed = false;
    }
  }

  // =========================================================================
  // Drag handlers (BaseTool drag system)
  // =========================================================================

  public onDragStart(data: DragEventData): void {
    if (data.button !== 0) return;

    this.onFirstDragDetected();
  }

  public onDrag(data: DragEventData): void {
    if (!this.draggedSphere) return;

    this.setHoveredLineIntersect();
    this.setHoveredSphere();

    const mouse = this.getMouseFromEvent(data.event);
    const location = this.getPointUnderMouse(mouse, this.shiftKeyPressed);
    if (location) {
      this.onDragPoint(location);
    }

    this.updateVisuals();
  }

  public onDragEnd(_data: DragEventData): void {
    if (!this.draggedSphere) return;
    this.draggedSphere.unHighlight();
    this.onDragPointEnd();
  }
}
